// eBay CSV -> core orders. Ported from ebay.gs.
// eBay prepends junk rows and appends a footer; the real header contains
// "Sales record number" + "Order number". Rows sharing a Sales record number
// are one order (multi-line). Tracking number is newly mapped (confirmed).

import { SOURCE } from '../constants';
import { parseCsvText } from './parseCsv';
import { decodeHtmlEntities, cleanProduct, cleanPostcode, parseMoney, normalisePhone, removeAddressDuplicates } from '../lib/clean';
import { parseEbayDate } from '../lib/dates';
import { ebayDeliveryService } from '../lib/delivery';

function makeGetter(headerRow) {
  const idx = {};
  headerRow.forEach((h, i) => {
    idx[String(h).trim().toLowerCase()] = i;
  });
  return (row, name) => {
    const i = idx[name.toLowerCase()];
    return i == null ? '' : row[i] ?? '';
  };
}

function rowToObj(headerRow, row) {
  const obj = {};
  headerRow.forEach((h, i) => {
    obj[h] = row[i] ?? '';
  });
  return obj;
}

// eBay item title -> variation (the bracketed "[...]" part).
function ebayVariation(title) {
  return decodeHtmlEntities(
    (title.match(/\[.*?\]/g) || [])
      .join('')
      .replace(/\[|\]/g, '')
      .replace(/,/g, ' / ')
      .trim()
  );
}

export function parseEbayOrders(text, rules) {
  let rows = parseCsvText(text);
  if (!rows.length) return [];

  // Drop eBay's leading junk rows (index 0 & 2) and the trailing footer (last 3).
  rows = rows.filter((_, i) => i !== 0 && i !== 2);
  rows = rows.slice(0, Math.max(0, rows.length - 3));

  // Locate the real header row.
  const headerRowIndex = rows.findIndex(
    (row) =>
      Array.isArray(row) &&
      row.some((c) => String(c).trim().toLowerCase() === 'sales record number') &&
      row.some((c) => String(c).trim().toLowerCase() === 'order number')
  );
  if (headerRowIndex === -1) return [];

  const header = rows[headerRowIndex];
  const get = makeGetter(header);

  const groups = new Map(); // Sales record number -> rows
  for (let i = headerRowIndex + 1; i < rows.length; i++) {
    const row = rows[i];
    if (!row || row.every((c) => !c || String(c).trim() === '')) continue;
    // Footer guard.
    if (
      row.some((c) => {
        const s = String(c).toLowerCase();
        return s.includes('record(s) downloaded') || s.includes('seller id:');
      })
    ) {
      break;
    }
    const srn = String(get(row, 'Sales record number')).trim();
    if (!srn) continue;
    if (!groups.has(srn)) groups.set(srn, []);
    groups.get(srn).push(row);
  }

  const orders = [];
  for (const [srn, itemRows] of groups) {
    const first = itemRows[0];
    const country = get(first, 'Post to country');

    const lineItems = itemRows.map((row, n) => {
      const title = decodeHtmlEntities(get(row, 'Item title'));
      return {
        lineId: `li_${n + 1}`,
        product: cleanProduct(title),
        productRaw: get(row, 'Item title'),
        variation: ebayVariation(get(row, 'Item title')),
        buyerNotes: decodeHtmlEntities(get(row, 'Buyer note')),
        quantity: Number(get(row, 'Quantity')) || 1,
        unitPrice: parseMoney(get(row, 'Sold for')),
        sku: get(row, 'Custom label') || '',
        isLeather: title.toLowerCase().includes('leather'),
        sortOrder: n,
      };
    });

    const postage = itemRows.reduce((sum, r) => sum + parseMoney(get(r, 'Postage and packaging')), 0);
    const iossRef = itemRows
      .map((r) => get(r, 'eBay reference name'))
      .find((v) => String(v).trim() === 'IOSS');
    const tracking = itemRows.map((r) => get(r, 'Tracking number')).find((v) => String(v).trim()) || '';

    let shipping = {
      name: decodeHtmlEntities(get(first, 'Post to name')),
      phone: normalisePhone(get(first, 'Post to phone')),
      address1: decodeHtmlEntities(get(first, 'Post to address 1')),
      address2: decodeHtmlEntities(get(first, 'Post to address 2'))
        .replace(/\bEBAY\w*\b/gi, '')
        .replace(/\s+/g, ' ')
        .trim(),
      townCity: decodeHtmlEntities(get(first, 'Post to city')),
      county: decodeHtmlEntities(get(first, 'Post to county')),
      postcode: cleanPostcode(get(first, 'Post to postcode'), country),
      country,
      service: ebayDeliveryService(get(first, 'Delivery service'), rules?.ebay),
      ioss: !!iossRef,
    };
    shipping = removeAddressDuplicates(shipping);

    orders.push({
      source: SOURCE.EBAY,
      platformOrderId: srn,
      platformOrderId2: String(get(first, 'Order number')).trim(),
      buyer: {
        userId: decodeHtmlEntities(get(first, 'Buyer username')),
        name: decodeHtmlEntities(get(first, 'Buyer name')),
        email: decodeHtmlEntities(get(first, 'Buyer email')),
      },
      shipping,
      lineItems,
      money: {
        currency: 'GBP',
        postage,
        discount: 0,
        refund: 0,
      },
      dates: {
        ordered: parseEbayDate(get(first, 'Sale date')),
        paid: parseEbayDate(get(first, 'Paid on date')) || null,
        postByPlatform: parseEbayDate(get(first, 'Post by date')) || null,
      },
      workflow: { trackingNumber: tracking },
      raw: { ebay: itemRows.map((r) => rowToObj(header, r)) },
    });
  }

  return orders;
}
